package modysy.simulation.collision

import modysy.model.Transform

import modysy.model.GameObject

import scala.collection.mutable.ListBuffer

object CollisionPenetrationResolver {

  /** Parameter specifying the strength of positional correction in percents. */
  var positionalCorrectionPercent: Double = 1.3

  /** Parameter specifying the threshold for performing positional correction. Higher means more penetration will be
    * tolerated.
    */
  var positionalCorrectionSlop: Double = 0

  /* Resolve penetrations of agents, resulting in objects with velocity pushing other objects.
   *
   * @param collisions A sequence of collision pairs.
   * @return Sequence of pairs of objects, for which a collusion resolution was performed.
   */
  def resolve(collisions: Seq[CollisionPair]): Seq[(GameObject, GameObject)] = {
    val resolved = Seq.newBuilder[(GameObject, GameObject)]

    for (CollisionPair(first, second, collisionSpot) <- collisions) {
      val invM1 = first.body.inverseMass
      val invM2 = second.body.inverseMass
      val v1 = first.body.velocity.linear
      val v2 = second.body.velocity.linear
      val x1 = first.body.pose.translation
      val x2 = second.body.pose.translation
      val invI1 = first.body.inverseInertia
      val invI2 = second.body.inverseInertia
      val rotation90 = Transform.rotate(math.Pi / 2)
      val r1 = rotation90 * (collisionSpot.point - x1)
      val r2 = rotation90 * (collisionSpot.point - x2)
      val normal = collisionSpot.normal

      val relativeVelocityAlongNormal = (v2 - v1).dot(normal)

      val e = math.min(
        first.body.material.restitution,
        second.body.material.restitution
      )
      var j = -(1 + e) * relativeVelocityAlongNormal / (normal.squaredNorm * (invM1 + invM2) +
        math.pow(r1.dot(normal), 2) * invI1 +
        math.pow(r2.dot(normal), 2) * invI2)

      // scroll down to "Sinking objects"
      // https://gamedevelopment.tutsplus.com/tutorials/how-to-create-a-custom-2d-physics-engine-the-basics-and-impulse-resolution--gamedev-6331
      val correction = Math.max(
        collisionSpot.penetration - positionalCorrectionSlop,
        0
      ) / (invM1 + invM2) * positionalCorrectionPercent

      first.body.pose.translation -= invM1 * correction * normal
      second.body.pose.translation += invM2 * correction * normal

      resolved += ((first, second))
    }

    resolved.result()
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/simulation/collision/CollisionPenetrationResolver.scala